/**
 * EditorSelection: visual feedback for the currently selected object.
 *
 * Renders three things around the selected object:
 *   - a cyan wireframe bounding box sized to its marker,
 *   - a translucent sphere showing its trigger radius (when it has one),
 *   - a DOM name label projected to screen space (updated each frame).
 *
 * Selection itself is driven by `EditorState.selectedId`; this class only draws
 * the feedback and re-syncs when the selection (or the underlying object's
 * marker/transform) changes. The editor calls `pick(state)` is on the manager;
 * here we just visualize.
 */

import { BLEND_NORMAL, Entity, StandardMaterial, Vec3 } from 'playcanvas';
import type { Application } from 'playcanvas';
import type { CameraState } from '../player/types';
import type { SceneObjectManager } from '../scene/SceneObjectManager';
import type { EditorState } from './EditorState';
import type { SceneObject } from '../scene/SceneObjectTypes';
import { overlayIds } from '../render/LayerManager';

const worldPos = new Vec3();
const screen = new Vec3();

export class EditorSelection {
    private _app: Application;
    private _camera: Entity;
    private _state: EditorState;
    private _getMgr: () => SceneObjectManager | null;

    private _bbox: Entity | null = null;
    private _range: Entity | null = null;
    private _label: HTMLElement;
    private _curId: string | null = null;

    constructor(
        app: Application,
        camera: Entity,
        state: EditorState,
        getMgr: () => SceneObjectManager | null
    ) {
        this._app = app;
        this._camera = camera;
        this._state = state;
        this._getMgr = getMgr;

        this._label = document.createElement('div');
        this._label.className = 'sel-label hidden';
        document.body.appendChild(this._label);
    }

    /** Select an object by id (or null to clear). Rebuilds visuals if changed. */
    select(id: string | null): void {
        if (this._curId === id) { this.refresh(); return; }
        this._curId = id;
        this._clearVisuals();
        if (!id) {
            this._label.classList.add('hidden');
            return;
        }
        this._buildVisuals();
    }

    /** Rebuild visuals for the current selection (after a marker/transform edit). */
    refresh(): void {
        this._clearVisuals();
        if (this._curId) this._buildVisuals();
    }

    /** Move the bbox/range to follow a live transform without a full rebuild —
     *  called every gizmo drag frame so the selection cage tracks the marker.
     *  The bbox also tracks the object's rotation so an oriented box (portal /
     *  trigger zone / pitched panel) stays aligned with its marker. */
    follow(o: SceneObject): void {
        if (!this._curId || this._curId !== o.id) return;
        const c = o.transform.position;
        const rot = o.transform.rotation;
        const scale = markerScaleVec(o);
        if (this._bbox) {
            this._bbox.setPosition(c[0], c[1], c[2]);
            this._bbox.setLocalEulerAngles(rot[0], rot[1], rot[2]);
            this._bbox.setLocalScale(scale[0] * 1.08, scale[1] * 1.08, scale[2] * 1.08);
        }
        if (this._range) this._range.setPosition(c[0], c[1], c[2]);
    }

    /** Per-frame: keep the name label on the object's screen position. The
     *  label text is re-read every frame so renames in the property panel show
     *  up immediately (no rebuild needed). */
    update(_state: CameraState): void {
        if (!this._curId) return;
        const mgr = this._getMgr();
        const o = mgr?.get(this._curId);
        if (!o) { this._label.classList.add('hidden'); return; }
        this._label.textContent = o.name;

        const c = o.transform.position;
        worldPos.set(c[0], c[1], c[2]);

        // Behind-camera test.
        const cp = this._camera.getPosition();
        const f = this._camera.forward;
        const dx = worldPos.x - cp.x;
        const dy = worldPos.y - cp.y;
        const dz = worldPos.z - cp.z;
        if (dx * f.x + dy * f.y + dz * f.z <= 0) {
            this._label.classList.add('hidden');
            return;
        }

        this._camera.camera!.worldToScreen(worldPos, screen);
        const w = window.innerWidth;
        const h = window.innerHeight;
        if (screen.x < 0 || screen.x > w || screen.y < 0 || screen.y > h) {
            this._label.classList.add('hidden');
            return;
        }
        this._label.style.left = `${screen.x}px`;
        this._label.style.top = `${screen.y - 18}px`;
        this._label.classList.remove('hidden');
    }

    private _buildVisuals(): void {
        const mgr = this._getMgr();
        const o = this._curId ? mgr?.get(this._curId) : undefined;
        if (!o) return;

        const c = o.transform.position;
        const rot = o.transform.rotation;
        const scale = markerScaleVec(o);

        // Bounding box (wireframe cyan), slightly larger than the marker.
        // Inherits the object's rotation so the cage aligns with oriented
        // markers (portals / trigger zones / pitched image panels).
        const bbox = new Entity('sel-bbox');
        bbox.addComponent('render', { type: 'box' });
        bbox.render!.material = wireframeMaterial([0.3, 0.9, 1.0]);
        bbox.render!.layers = overlayIds();
        bbox.setLocalScale(scale[0] * 1.08, scale[1] * 1.08, scale[2] * 1.08);
        bbox.setLocalEulerAngles(rot[0], rot[1], rot[2]);
        bbox.setPosition(c[0], c[1], c[2]);
        this._app.root.addChild(bbox);
        this._bbox = bbox;

        // Trigger range sphere (translucent), when the object has a radius.
        const r = o.interaction?.radius;
        if (r != null && r > 0) {
            const range = new Entity('sel-range');
            range.addComponent('render', { type: 'sphere' });
            range.render!.material = translucentMaterial([0.3, 0.9, 1.0], 0.12);
            range.render!.layers = overlayIds();
            range.setLocalScale(r * 2, r * 2, r * 2);
            range.setPosition(c[0], c[1], c[2]);
            this._app.root.addChild(range);
            this._range = range;
        }

        this._label.textContent = o.name;
    }

    private _clearVisuals(): void {
        if (this._bbox) { this._app.root.removeChild(this._bbox); this._bbox.destroy(); this._bbox = null; }
        if (this._range) { this._app.root.removeChild(this._range); this._range.destroy(); this._range = null; }
    }

    destroy(): void {
        this._clearVisuals();
        this._label.remove();
    }
}

/** Marker size as a 3-vector (primitive uses scale[]; others use uniform scale). */
function markerScaleVec(o: SceneObject): [number, number, number] {
    const m = o.marker;
    if (m.kind === 'primitive' || m.kind === 'video' || m.kind === 'imagePanel') {
        const s = m.scale ?? [1, 1, 1];
        return [s[0], s[1], s[2]];
    }
    const s = m.scale ?? 1;
    return [s, s, s];
}

function wireframeMaterial(color: [number, number, number]): StandardMaterial {
    // Translucent emissive outline (wireframe is per-meshInstance in PlayCanvas;
    // a low-opacity box reads clearly as a selection cage around the marker).
    const mat = new StandardMaterial();
    mat.emissive.set(color[0], color[1], color[2]);
    mat.emissiveIntensity = 1.4;
    mat.diffuse.set(0, 0, 0);
    mat.useLighting = false;
    mat.blendType = BLEND_NORMAL;
    mat.opacity = 0.22;
    mat.depthWrite = false;
    mat.depthTest = false;
    mat.update();
    return mat;
}

function translucentMaterial(color: [number, number, number], opacity: number): StandardMaterial {
    const mat = new StandardMaterial();
    mat.emissive.set(color[0], color[1], color[2]);
    mat.emissiveIntensity = 0.6;
    mat.diffuse.set(0, 0, 0);
    mat.useLighting = false;
    mat.blendType = BLEND_NORMAL;
    mat.opacity = opacity;
    mat.depthWrite = false;
    mat.depthTest = false;
    mat.update();
    return mat;
}
